/**
 * Leaguemate Scouting — S1 format-similarity (pure functions, no I/O).
 *
 * Ported verbatim from the validated audit formula (LEAGUEMATE_SCOUTING_v1.md
 * §5; audit script docs/planning/scout_audit/audit_leaguemates.py). Weights are
 * config (scoutConfig.js), not magic numbers.
 *
 * Field shapes confirmed against real cached Sleeper JSON (2026-05-31): both
 * the target `/league/{id}` object and each `/user/{uid}/leagues/nfl/{season}`
 * entry carry `roster_positions`, `scoring_settings`, and `total_rosters`, so
 * one profile extractor serves both.
 */

import {
  SIM_WEIGHTS,
  SIM_FLOOR,
  MAX_LEAGUES_PER_MGR,
  TX_LEAGUES_PER_MGR,
} from "./scoutConfig.js";

export function countSuperFlex(rosterPositions) {
  return (rosterPositions || []).filter((s) => s === "SUPER_FLEX").length;
}

export function pprValue(scoringSettings) {
  // `rec` is the PPR points-per-reception (0 / 0.5 / 1.0). May be absent.
  return (scoringSettings || {}).rec || 0;
}

export function tepValue(scoringSettings) {
  // `bonus_rec_te` > 0 means TE-premium is on.
  return (scoringSettings || {}).bonus_rec_te || 0;
}

/**
 * Reduce any league-like object to the five fields similarity compares.
 * Works on both the target league object and a user-leagues entry.
 */
export function toFormatProfile(league) {
  return {
    superFlex: countSuperFlex(league?.roster_positions),
    ppr:       pprValue(league?.scoring_settings),
    tep:       tepValue(league?.scoring_settings),
    rosterLen: (league?.roster_positions || []).length,
    teamCount: league?.total_rosters || 0,
  };
}

/**
 * Format similarity in [0,1]. 1.0 = identical format; QB-slot mismatch
 * dominates the penalty. Inputs are format profiles (see toFormatProfile).
 */
export function computeSimilarity(target, other) {
  const w = SIM_WEIGHTS;
  let s = 1.0;
  s -= w.superflex * Math.abs(target.superFlex - other.superFlex);
  s -= w.ppr       * Math.abs(target.ppr - other.ppr);
  s -= w.tep       * ((target.tep > 0) !== (other.tep > 0) ? 1 : 0);
  s -= w.rosterLen * (Math.abs(target.rosterLen - other.rosterLen) / 10);
  s -= w.teamCount * (Math.abs(target.teamCount - other.teamCount) / 12);
  return Math.max(0, Math.min(1, s));
}

/**
 * Rank a manager's candidate source leagues against the target, drop those
 * below the similarity floor, sort by similarity desc (deterministic
 * tie-break on league_id), cap to MAX_LEAGUES_PER_MGR (affinity set) and mark
 * the top TX_LEAGUES_PER_MGR as the transaction set (consumed in S3).
 *
 * CRITICAL (v1 spec §7b/§7c measurement carries): the returned `sampled` array
 * is the canonical record of *exactly which leagues were sampled and how many*.
 * Later FAAB-%, trade-count, and net-pick math MUST normalize over this
 * identical set — so we surface the counts the downstream normalizers need.
 *
 * @param targetProfile  format profile of the target league
 * @param leagues        candidate source-league objects (already deduped)
 * @returns { sampled: Array<{league, league_id, similarity, inTxSet}>, counts }
 */
export function rankAndCapLeagues(targetProfile, leagues, opts = {}) {
  const floor      = opts.simFloor    ?? SIM_FLOOR;
  const maxLeagues = opts.maxLeagues  ?? MAX_LEAGUES_PER_MGR;
  const txLeagues  = opts.txLeagues   ?? TX_LEAGUES_PER_MGR;

  const scored = (leagues || [])
    .map((l) => ({ league: l, similarity: computeSimilarity(targetProfile, toFormatProfile(l)) }))
    .filter((x) => x.similarity >= floor)
    .sort(
      (a, b) =>
        b.similarity - a.similarity ||
        String(a.league.league_id).localeCompare(String(b.league.league_id))
    );

  const sampled = scored.slice(0, maxLeagues).map((x, i) => ({
    league:     x.league,
    league_id:  x.league.league_id,
    similarity: Math.round(x.similarity * 1000) / 1000,
    inTxSet:    i < txLeagues,
  }));

  return {
    sampled,
    counts: {
      candidates: (leagues || []).length, // before floor
      aboveFloor: scored.length,          // passed similarity floor
      sampled:    sampled.length,         // affinity set (== min(aboveFloor, max))
      txSet:      sampled.filter((s) => s.inTxSet).length, // S3 transaction set
    },
  };
}
